import About from '../views/About/About.vue'
import ApplicationView from '../views/ApplicationView.vue'
import AppSettings from '../views/AppSettings/AppSettings.vue'
import Configuration from '../agilis/util/Configuration'
import Home from '../views/Home/Home.vue'
import Playground from '../views/Playground/Playground.vue'
import SettingsVue from '../views/Settings/Settings.vue'
import store from '@/store'
import Vue from 'vue'
import VueRouter from 'vue-router'

import { updateMeta } from './metaHelpers'
import { getKeycloak } from '@/plugins/keycloak'
import ComponentResolverPublic from '@/agilis/public/components/ComponentResolverPublic/ComponentResolverPublic.vue'

Vue.use(VueRouter)

const routes = [
  {
    path: '/application/:applicationId/public/tracker/:trackerId',
    component: ComponentResolverPublic
  },
  /**
   * Playground for prototypes and internal tools
   */
  {
    path: '/playground/:tool?',
    name: 'playground',
    component: Playground,
    props: true,
    meta: {
      title: 'Playground ARPA'
    }
  },
  /**
   * Page summarizing to the user the Agilis application deployed and configuration/artifact version.
   * This page also highlights at a glance the misalignment of the configurations between agilis-svc-configurator
   * and the other services
   *
   * For ADMIN users also provides
   * - access to an editor of the configuration
   * - user impersonation
   */
  {
    path: '/about',
    name: 'about',
    component: About,
    meta: {
      title: 'About ARPA'
    }
  },
  /**
   * User settings page
   */
  {
    path: '/settings',
    name: 'settings',
    component: SettingsVue,
    meta: {
      title: 'ARPA Settings'
    }
  },
  /**
   * Application settings page
   */
  {
    path: '/application/:applicationId/settings/:tabId/:trackerTypeId?/:mode?/:trackerId?',
    alias: [
      '/application/:applicationId/settings/:tabId/:trackerTypeId'
    ],
    name: 'applicationSettings',
    component: AppSettings,
    props: true,
    meta: {
      title: 'ARPA - :applicationName - settings'
    }
  },
  /**
   * Route providing access to Application Workspaces (for trackers: datatable, view, edit, dashboard)
   *
   * Supported `tabId`: elements of the TabBar below the application header
   * - tracker: default tab giving access to the datatable and its views (kanban/dashboard)
   * - dashboard: dashbaord at workspace level (defined at configuration level)
   * - (coming soon) settings: workspace settings, i.e. manage users, dataTable overrides, notifications
   *
   * When tabId=tracker, also the following parameters are available:
   * - trackerTypeId: the trackerType id
   * - mode: datatable mode [select, view, edit]
   * - trackerId: id of the tracker
   */
  {
    path: '/application/:applicationId/workspace/:workspaceId/:tabId/:trackerTypeId?/:mode?/:trackerId?',
    alias: [
      '/application/:applicationId/workspace/:workspaceId/:tabId/:trackerTypeId',
      '/application/:applicationId/workspace/:workspaceId/:tabId',
      '/application/:applicationId/workspace/:workspaceId'
    ],
    name: 'applicationWorkspace',
    component: ApplicationView,
    props: true,
    meta: {
      title: 'ARPA - :applicationName - :workspaceName'
    }
  },
  /**
   * Route providing access to the Application Overview
   *
   * Supported `tabId`: elements of the TabBar below the application header
   * - dashboard: dashboard at application level (defined at configuration level)
   * - (coming soon) settings: application settings, i.e. create/remove workspaces, roles and permissions, dataTable
   */
  {
    path: '/application/:applicationId/:tabId?',
    name: 'applicationOverview',
    component: ApplicationView,
    props: true,
    meta: {
      title: 'ARPA - :applicationName'
    }
  },
  /**
   * Agilis Home page presenting the available application/workspaces
   */
  {
    path: '/',
    name: 'home',
    component: Home,
    meta: {
      title: 'ARPA'
    }
  },
  /**
   * Fallback route, when none of the above matches
   */
  {
    path: '*',
    name: 'notfound',
    component: About,
    meta: {
      title: 'About ARPA'
    }
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

router.beforeEach(async (to, from, next) => {
// Here we need to retrieve the Keycloak instance directly because vuekeycloak-js is not yet defined
  const keycloak = getKeycloak()
  if (to.matched && to.matched.some(record => record.path.includes('/public/'))) {
    return next()
  } else {
    if (window.envmap.VUE_APP_AGILIS_KEYCLOAK_REQUIRED && (!keycloak || !keycloak.authenticated)) {
      keycloak.login()
      return
    }
    if (to.name === 'playground') {
      if (!store.getters['userDataStore/userIsAdmin']) {
        router.agilis.home()
      }
    }
    updateMeta(to)
    next()
  }
})

function getContext ({ trackerTypeId = null, workspaceId = null, applicationId = null } = {}) {
  return {
    applicationId: applicationId !== null ? applicationId : store.getters['contextStore/applicationId'],
    workspaceId: workspaceId != null ? workspaceId : store.getters['contextStore/workspaceId'],
    trackerTypeId: trackerTypeId != null ? trackerTypeId : store.getters['contextStore/trackerTypeId']
  }
}

router.agilis = {
  manageRoute (to) {
    const defaultMode = 'select'
    const { applicationId, workspaceId, trackerTypeId, trackerId, mode, tabId } = to.params
    const { parentTrackerId } = to.query
    const routeName = to.name

    const selectedContext = {
      applicationId: applicationId,
      workspaceId: workspaceId,
      trackerId: trackerId,
      parentTrackerId
    }

    let routeTrackerTypeId = trackerTypeId
    let routeTabId = tabId
    if (applicationId != null && !routeTrackerTypeId) {
      // TODO in report tab trackerType ids are report ids
      // it could be good to refactor so that horiz and vertical tabs
      // are managed using generic horizTabId and vertTabId
      if (routeName === 'applicationSettings') {
        const defaultSetting = Configuration.settings.getDefaultApplicationSettings(applicationId)
        if (defaultSetting) {
          routeTabId = defaultSetting.tabId
          routeTrackerTypeId = defaultSetting.id
        }
      } else if (tabId) {
        switch (tabId) {
          case 'tracker':
            routeTrackerTypeId = Configuration.auth.getDefaultTrackerTypeId(applicationId, workspaceId)
            break

          case 'report':
            routeTrackerTypeId = Configuration.auth.getDefaultReportId(applicationId, workspaceId)
            break

          case 'document':
            routeTrackerTypeId = Configuration.document.getDefaultDocumentId(applicationId, workspaceId)
            break

          case 'dashboard':
            if (workspaceId) {
              routeTrackerTypeId = Configuration.auth.getDefaultDashboardId(applicationId, workspaceId)
            }
            break

          default:
            routeTrackerTypeId = null
        }
      }

      if (routeTrackerTypeId) {
        // reloadId parameter is a timestamp to force component reloading
        // when the route or component data does not change. we can then pass the reloadId
        // as a key prop to the component that should be reloaded, e.g. TrackerDataTable
        router.push({
          name: routeName,
          params: {
            applicationId: applicationId,
            workspaceId: workspaceId,
            trackerTypeId: routeTrackerTypeId,
            tabId: routeTabId,
            mode: defaultMode,
            reloadId: Date.now()
          }
        })
      } else {
        // TODO: Vedere come gestire se non sono presenti tipi di tracker
        routeTrackerTypeId = null
      }
    }

    let routeMode = mode
    if (routeMode == null && routeTrackerTypeId != null) {
      routeMode = defaultMode
    }

    selectedContext.trackerTypeId = routeTrackerTypeId
    selectedContext.mode = routeMode
    selectedContext.baseURL = Configuration.getApplicationBaseURL(applicationId)
    selectedContext.tabId = routeTabId

    if (Configuration.getApplication(applicationId)) {
      // working in a valid application context
      store.commit('contextStore/setSelectedContext', selectedContext)
    } else if (applicationId) {
      // invalid application id
      store.commit('contextStore/setSelectedContext', {})
      router.push({ name: 'home' })
    } else {
      store.commit('contextStore/setSelectedContext', {})
    }
  },
  home: () => {
    router.push({ name: 'home' })
  },
  about: () => {
    router.push({ name: 'about' })
  },
  playground: (tool) => {
    router.push({ name: 'playground', params: { tool } })
  },
  workspace: (applicationId, workspaceId) => {
    if (applicationId === 'playground') return router.agilis.playground(workspaceId)

    let tabId = 'tracker'
    const tabs = Configuration.getApplicationTabs(applicationId, workspaceId)
    if (tabs && !tabs.find(tab => tab.id === 'tracker')) {
      tabId = Configuration.getApplicationDefaultTab(applicationId, workspaceId)
    }

    let trackerTypeId
    if (tabId === 'tracker') {
      trackerTypeId = Configuration.auth.getDefaultTrackerTypeId(applicationId, workspaceId)
    }

    router.push({
      name: 'applicationWorkspace',
      params: {
        applicationId: applicationId,
        workspaceId: workspaceId,
        tabId,
        trackerTypeId
      }
    })
  },
  application: (applicationId) => {
    router.push({
      name: 'applicationOverview',
      params: {
        applicationId: applicationId,
        tabId: Configuration.getApplicationDefaultTab(applicationId)
      }
    })
  },
  settings: ({ applicationId }) => {
    router.push({
      name: 'applicationSettings',
      params: {
        applicationId: applicationId,
        routeName: 'applicationSettings'
      }
    })
  },
  viewTab: ({ applicationId, tabId, workspaceId }) => {
    const defaultTab = Configuration.getApplicationDefaultTab(applicationId, workspaceId)

    router.push({
      name: workspaceId ? 'applicationWorkspace' : 'applicationOverview',
      params: {
        applicationId,
        workspaceId,
        tabId: tabId || defaultTab
      }
    })
  },
  viewTrackerTable: (trackerTypeId) => {
    // reloadId parameter is a timestamp to force component reloading
    // when the route or component data does not change. we can then pass the reloadId
    // as a key prop to the component that should be reloaded, e.g. TrackerDataTable
    router.push({
      name: 'applicationWorkspace',
      params: {
        ...getContext({ trackerTypeId }),
        trackerId: null,
        mode: 'select',
        reloadId: Date.now()
      }
    })
  },
  newTracker: (trackerTypeId, parentTrackerId, parentDTO) => {
    router.push({
      name: 'applicationWorkspace',
      params: {
        ...getContext({ trackerTypeId }),
        trackerId: 'new',
        mode: 'insert',
        parentDTO: parentDTO
      },
      query: {
        parentTrackerId
      }
    })
  },
  copyTracker: ({ trackerTypeId, data, parentTrackerId, next }) => {
    router.push({
      name: 'applicationWorkspace',
      params: {
        ...getContext({ trackerTypeId }),
        trackerId: 'new',
        mode: 'insert',
        data,
        next
      },
      query: {
        parentTrackerId
      }
    })
  },
  viewTracker: ({ applicationId, workspaceId, trackerId, trackerTypeId, parentTrackerId, next }) => {
    // reloadId parameter is a timestamp to force component reloading
    // when the route or component data does not change. we can then pass the reloadId
    // as a key prop to the component that should be reloaded, e.g. TrackerDataTable
    router.push({
      name: 'applicationWorkspace',
      params: {
        ...getContext({ trackerTypeId, applicationId, workspaceId }),
        trackerId: trackerId,
        mode: 'view',
        tabId: 'tracker',
        reloadId: Date.now(),
        next
      // }, // Commentato perchè il parentTrackerId in questa view non serve
      // query: {
      //   parentTrackerId
      }
    })
  },
  viewTrackerDTO: (trackerDTO) => {
    router.push({
      name: 'applicationWorkspace',
      params: {
        applicationId: trackerDTO.applicationId,
        workspaceId: trackerDTO.workspaceId,
        trackerTypeId: trackerDTO.trackerTypeId,
        trackerId: trackerDTO.id,
        mode: 'view'
      // }, // Commentato perchè il parentTrackerId in questa view non serve
      // query: {
      //   parentTrackerId: trackerDTO.parentId
      }
    })
  },
  editTracker: ({ trackerId, trackerTypeId, next }) => {
    router.push({
      name: 'applicationWorkspace',
      params: {
        ...getContext({ trackerTypeId }),
        trackerId: trackerId,
        mode: 'edit',
        next
      }
    })
  },
  view: ({ applicationId, trackerTypeId, trackerId, routeName, next }) => {
    router.push({
      name: routeName,
      params: {
        ...getContext({ trackerTypeId, applicationId }),
        trackerId: trackerId,
        mode: 'view',
        reloadId: Date.now(),
        next
      }
    })
  },
  edit: ({ trackerTypeId, trackerId, routeName, next }) => {
    router.push({
      name: routeName,
      params: {
        ...getContext({ trackerTypeId }),
        trackerId: trackerId,
        mode: 'edit',
        next
      }
    })
  },
  create: ({ trackerTypeId, parentTrackerId, routeName }) => {
    router.push({
      name: routeName,
      params: {
        ...getContext({ trackerTypeId }),
        trackerId: 'new',
        mode: 'insert'
      },
      query: {
        parentTrackerId
      }
    })
  },
  copy: ({ trackerTypeId, data, parentTrackerId, routeName, next }) => {
    router.push({
      name: routeName,
      params: {
        ...getContext({ trackerTypeId }),
        trackerId: 'new',
        mode: 'insert',
        data,
        next
      },
      query: {
        parentTrackerId
      }
    })
  },
  viewTable: (trackerTypeId, routeName) => {
    router.push({
      name: routeName,
      params: {
        ...getContext({ trackerTypeId }),
        trackerId: null,
        mode: 'select',
        reloadId: Date.now()
      }
    })
  },

  /**
   * @returns Return the data object attached to the current route
   */
  getRouteData () {
    return router.history?.current?.params?.data
  },

  /**
   * @returns Return the next routing callback for the current route, for example for conditional routing
   */
  getRouteNextRoute () {
    const nextCallback = router.history?.current?.params?.next
    return nextCallback
  },

  // ///////////////////////////////////////////////////////////////////////////////////////////////
  // URL for href
  // ///////////////////////////////////////////////////////////////////////////////////////////////
  urlApp: (applicationId) => {
    const defaultTab = Configuration.getApplicationDefaultTab(applicationId)
    return `/application/${applicationId}/${defaultTab}`
  },

  urlAppTab: (applicationId, tabId) => {
    return `/application/${applicationId}/${tabId}`
  },

  urlWorkspace: (applicationId, workspaceId) => {
    if (applicationId === 'playground') return `/playground/${workspaceId}`
    return `/application/${applicationId}/workspace/${workspaceId}`
  },

  urlTracker: ({
    applicationId = null,
    trackerId = null,
    trackerTypeId, // for context override
    mode = null,
    workspaceId = null
  } = {}) => {
    const { applicationId: _applicationId, workspaceId: _workspaceId, trackerTypeId: _trackerTypeId } = getContext({ trackerTypeId, workspaceId, applicationId })
    const base = `/application/${_applicationId}/workspace/${_workspaceId}/tracker/${_trackerTypeId}`

    if (mode && trackerId) { // view, edit etc
      return `${base}/${mode}/${trackerId}`
    }
    return base
  },

  urlTab: ({ tabId }) => {
    const { applicationId, workspaceId } = getContext()
    const base = workspaceId ? `/application/${applicationId}/workspace/${workspaceId}/${tabId}` : `/application/${applicationId}/${tabId}`
    return base
  },

  urlTrackerTable: (trackerTypeId) => {
    return router.agilis.urlTracker({ trackerTypeId })
  },

  urlViewTracker: ({ applicationId, trackerId, trackerTypeId, workspaceId }) => {
    return router.agilis.urlTracker({ applicationId, trackerId, trackerTypeId, mode: 'view', workspaceId })
  },

  urlEditTracker: ({ trackerId, trackerTypeId, workspaceId }) => {
    return router.agilis.urlTracker({ trackerId, trackerTypeId, mode: 'edit', workspaceId })
  }
}

export default router
